import MomentUtils from '@date-io/moment';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { StylesProvider, ThemeProvider } from '@material-ui/styles';
import React, { useContext, useEffect } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { routeForDashboard } from '../Routes';
import { AuthenticationContext, AuthenticationStatus } from './ApplicationAuthentication';
import { AuthenticationError } from '../Components/SplashScreens/AuthenticationError';
import { CookiesDisabledWarning } from '../Components/SplashScreens/CookiesDisabledWarning';
import { LoggedOut } from '../Components/SplashScreens/LoggedOut';
import { CookieStatus, useCookieChecker } from '../Hooks/useCookieChecker';
import { IdentityLoadingStatus, useIdentityLoader } from '../Hooks/useIdentityLoader';
import { LoadingStatus, usePermissionLoader } from '../Hooks/usePermissionLoader';
import { GlobalSnackbar } from '../Shared/GlobalSnackbar';
import { muiMedicationsTheme } from '../Styles/Theme';
import { createGenerateClassName } from '@material-ui/core/styles';
import { routerHistory } from './history';

const useAuthenticationFlow = (
    SuccessComponent: React.ComponentType,
    loadingSpinner: React.ReactNode
): React.ReactNode => {
    const splashScreen: React.ReactNode = loadingSpinner;
    const authenticationError: React.ReactNode = <AuthenticationError />;
    const permissionError: React.ReactNode = <AuthenticationError />;
    const loggedOutComponent: React.ReactNode = <LoggedOut key='Logged out' />;
    const cookiesDisabledComponent: React.ReactNode = <CookiesDisabledWarning key='cookies-disabled-warning' />;

    const cookieChecker = useCookieChecker();
    const authentication = useContext(AuthenticationContext);
    const permissions = usePermissionLoader();
    const identity = useIdentityLoader();

    useEffect(() => {
        if (cookieChecker.cookieStatus === CookieStatus.Safe) authentication.authenticate();
    }, [cookieChecker.cookieStatus]);

    useEffect(() => {
        if (authentication.status === AuthenticationStatus.Authenticated) identity.loadUser();
    }, [authentication.status]);

    useEffect(() => {
        if (identity.identityStatus === IdentityLoadingStatus.Loaded) permissions.loadPermissions();
    }, [identity.identityStatus]);

    if (cookieChecker.cookieStatus === CookieStatus.Checking) return splashScreen;
    else if (cookieChecker.cookieStatus === CookieStatus.Unsafe) return cookiesDisabledComponent;
    else if (cookieChecker.cookieStatus === CookieStatus.TimedOut) return authenticationError;
    else if (cookieChecker.cookieStatus === CookieStatus.Safe)
        if (authentication.status === AuthenticationStatus.LoggedOut) return loggedOutComponent;
        else if (authentication.status === AuthenticationStatus.Errored) return authenticationError;
        else if (authentication.status === AuthenticationStatus.Authenticated)
            if (identity.identityStatus === IdentityLoadingStatus.Loading) return splashScreen;
            else if (identity.identityStatus === IdentityLoadingStatus.Error) return permissionError;
            else if (identity.identityStatus === IdentityLoadingStatus.Loaded)
                if (permissions.permissionStatus === LoadingStatus.Loading) return splashScreen;
                else if (permissions.permissionStatus === LoadingStatus.Error) return permissionError;
                else if (permissions.permissionStatus === LoadingStatus.Loaded)
                    return (
                        <Route>
                            <SuccessComponent />
                        </Route>
                    );

    return splashScreen;
};

const generateClassName = createGenerateClassName({
    disableGlobal: true
});

export function ApplicationRoot(props: { root: React.ComponentType; loadingSpinner: React.ReactNode }) {
    const displayComponent = useAuthenticationFlow(props.root, props.loadingSpinner);

    return (
        <MuiPickersUtilsProvider utils={MomentUtils}>
            <StylesProvider injectFirst generateClassName={generateClassName}>
                <CssBaseline />
                <ThemeProvider theme={muiMedicationsTheme}>
                    <Router history={routerHistory}>
                        <GlobalSnackbar>
                            <Switch>
                                {displayComponent}
                                <Redirect to={routeForDashboard} />
                            </Switch>
                        </GlobalSnackbar>
                    </Router>
                </ThemeProvider>
            </StylesProvider>
        </MuiPickersUtilsProvider>
    );
}
